<template>
  <div v-if="visible" class="pdf-viewer-overlay">
    <div
      class="pdf-viewer"
      :class="{ dragging: isDragging }"
      ref="pdfViewer"
      :style="viewerStyle"
    >
      <div class="pdf-header" @mousedown.stop="startDrag">
        <span>{{ title }}</span>
        <div class="pdf-controls">
          <button @click="toggleFullscreen" class="buttonexpand">⛶</button>
          <button @click="$emit('close')" class="buttonclose">✖</button>
        </div>
      </div>

      <iframe :src="src" class="pdf-content" :style="iframeStyle"></iframe>

      <!-- Resize handles -->
      <div
        v-for="dir in directions"
        :key="dir"
        :class="['resize-handle', dir]"
        @mousedown.prevent="startResize(dir, $event)"
      ></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PDFViewer",
  props: {
    src: { type: String, required: true },
    visible: { type: Boolean, required: true },
    title: { type: String, default: "PDF Preview" },
  },
  data() {
    return {
      isDragging: false,
      isResizing: false,
      dragOffset: { x: 0, y: 0 },
      resizeDir: "",
      startPos: { x: 0, y: 0 },
      startSize: { width: 0, height: 0 },
      startCoords: { top: 0, left: 0 },
      position: { top: 50, left: 50 },
      size: { width: 800, height: 600 },
      directions: [
        "top",
        "right",
        "bottom",
        "left",
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
      ],
      isFullscreen: false,
      initialSize: { width: 800, height: 600 },
      initialPosition: { top: 50, left: 50 },
    };
  },
  computed: {
    viewerStyle() {
      return {
        top: this.position.top + "px",
        left: this.position.left + "px",
        width: this.size.width + "px",
        height: this.size.height + "px",
      };
    },
    iframeStyle() {
      return {
        pointerEvents: this.isResizing ? "none" : "auto",
      };
    },
  },
  methods: {
    onDrag(event) {
      if (!this.isDragging) return;
      this.position.left = event.clientX - this.dragOffset.x;
      this.position.top = event.clientY - this.dragOffset.y;
    },

    startDrag(event) {
      this.isDragging = true;
      this.dragOffset = {
        x: event.clientX - this.position.left,
        y: event.clientY - this.position.top,
      };
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.initialSize = { ...this.size };
        this.initialPosition = { ...this.position };
        this.size = {
          width: window.innerWidth * 0.9,
          height: window.innerHeight * 0.9,
        };
        this.position = {
          top: window.innerHeight * 0.05,
          left: window.innerWidth * 0.05,
        };
      } else {
        this.size = { ...this.initialSize };
        this.position = { ...this.initialPosition };
      }
      this.isFullscreen = !this.isFullscreen;
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },

    startResize(dir, event) {
      this.isResizing = true;
      this.resizeDir = dir;
      this.startPos = { x: event.clientX, y: event.clientY };
      this.startSize = { ...this.size };
      this.startCoords = { ...this.position };

      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
    },

    stopResize() {
      this.isResizing = false;
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);
    },

    onResize(event) {
      if (!this.isResizing) return;

      const dx = event.clientX - this.startPos.x;
      const dy = event.clientY - this.startPos.y;
      const dir = this.resizeDir;

      if (dir.includes("right")) {
        this.size.width = this.startSize.width + dx;
      }
      if (dir.includes("left")) {
        this.size.width = this.startSize.width - dx;
        this.position.left = this.startCoords.left + dx;
      }
      if (dir.includes("bottom")) {
        this.size.height = this.startSize.height + dy;
      }
      if (dir.includes("top")) {
        this.size.height = this.startSize.height - dy;
        this.position.top = this.startCoords.top + dy;
      }

      if (this.size.width < 200) this.size.width = 200;
      if (this.size.height < 150) this.size.height = 150;
    },
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopResize);
  },
};
</script>

<style scoped>
.pdf-viewer.dragging,
.pdf-viewer.dragging * {
  user-select: none !important;
}
.pdf-viewer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.pdf-viewer {
  position: absolute;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 0 10px #00000033;
  pointer-events: auto;
  min-height: 60px;
  min-width: 30px;
  resize: both;
}

.pdf-header {
  background-color: #f0f0f0;
  padding: 5px 10px;
  cursor: move;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ccc;
  height: 30px;
}
.pdf-controls {
  display: flex;
  gap: 5px;
}
.pdf-header button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
}
.pdf-content {
  width: 100%;
  height: calc(100% - 30px);
  border: none;
  overflow: hidden;
}

/* Resize handles */
.resize-handle {
  position: absolute;
  z-index: 10;
  background: transparent;
}

.resize-handle:hover {
  background: rgba(0, 0, 0, 0.1);
}

/* Sides */
.resize-handle.top {
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  cursor: ns-resize;
}
.resize-handle.bottom {
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  cursor: ns-resize;
}
.resize-handle.left {
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  cursor: ew-resize;
}
.resize-handle.right {
  top: 0;
  bottom: 0;
  right: 0;
  width: 4px;
  cursor: ew-resize;
}

/* Corners */
.resize-handle.top-left {
  top: 0;
  left: 0;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}
.resize-handle.top-right {
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}
.resize-handle.bottom-left {
  bottom: 0;
  left: 0;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}
.resize-handle.bottom-right {
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}
.pdf-viewer {
  position: absolute;
  top: 50px;
  left: 50px;
  width: 60vw;
  min-width: 20vw;
  height: 90vh;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 0 10px #00000033;
  resize: vertical;
}
.buttonexpand {
  font-size: 21px !important;
  transition: background-color 0.2s ease-in-out;
}
.buttonexpand:hover {
  background-color: #dadada;
}
.buttonclose {
  font-size: 16px !important;
  cursor: default !important;
}
.buttonclose:hover {
  background-color: #dadada;
}
</style>
